import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Tooltip,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as CompletedIcon,
  PendingActions as PendingIcon,
  PlayCircleFilled as StartedIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  QrCode as QrCodeIcon,
  Info as InfoIcon,
  LocationOn as LocationOnIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Navigation
} from '@mui/icons-material';
import DeviceInfoModal from './DeviceInfoModal';
import { useTranslation } from 'react-i18next';

// Görev zamanı chip bileşeni için stil tanımı
const TaskTimeChip = ({ status, label }: { status: string; label: string }) => (
  <Chip
    label={label}
    size="small"
    sx={{ 
      height: 20, 
      fontSize: '0.7rem', 
      bgcolor: `${status}20`, 
      color: status
    }}
  />
);

// TasksTable bileşeni için props arayüzü
interface TasksTableProps {
  tasks: any[];
  statusFilter: string;
  onShowTaskDetail: (task: any) => void;
  onOpenQrPrintModal: (task: any, event: React.MouseEvent) => void;
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  getTaskTimeColor: (task: any, timeString: string) => string;
  companyId: string | null;
  onOpenDeviceModal?: (deviceInfo: any) => void;
  taskType: string;
  branches?: {[key: string]: string}; // Şube bilgilerini içeren prop
  onRenewTask?: (taskId: string, adminNote: string) => Promise<void>; // Görevi yenileme fonksiyonu
  onOpenTaskLocationModal?: (task: any) => void; // Yeni prop
}

const TasksTable: React.FC<TasksTableProps> = ({ 
  tasks, 
  statusFilter,
  onShowTaskDetail, 
  onOpenQrPrintModal,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getTaskTimeColor,
  companyId,
  onOpenDeviceModal,
  taskType,
  branches,
  onRenewTask,
  onOpenTaskLocationModal // Yeni prop
}) => {
  const { t } = useTranslation('common2');
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Cihaz bilgileri için state
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<any>(null);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  
  // Gecikme açıklaması modalı için state
  const [delayReasonModalOpen, setDelayReasonModalOpen] = useState(false);
  const [selectedDelayReason, setSelectedDelayReason] = useState<string>("");
  
  // Red nedeni modalı için state
  const [rejectReasonModalOpen, setRejectReasonModalOpen] = useState(false);
  const [selectedRejectReason, setSelectedRejectReason] = useState<string>("");
  
  // Görev yenileme modalı için state
  const [renewTaskModalOpen, setRenewTaskModalOpen] = useState(false);
  const [selectedTaskToRenew, setSelectedTaskToRenew] = useState<any>(null);
  const [adminNote, setAdminNote] = useState<string>("");
  const [isRenewing, setIsRenewing] = useState(false);
  
  // Sayfalandırılmış task verileri
  const paginatedTasks = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    return tasks.slice(startIndex, startIndex + rowsPerPage);
  }, [tasks, page, rowsPerPage]);
  
  // Filtre değişikliklerinde sayfa numarasını sıfırla
  React.useEffect(() => {
    setPage(0);
  }, [tasks]);
  
  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Şube adını getir
  const getBranchName = (branchId: string | null) => {
    if (!branchId) return '-';
    
    // branchId bir nesne mi kontrol et ve düzenle
    if (typeof branchId === 'object' && branchId !== null) {
      const keys = Object.keys(branchId);
      if (keys.length > 0) {
        const firstKey = keys[0];
        
        branchId = firstKey;
      }
    }
    
    // Artık branchId bir string olmalı, şube adını döndür
    return branches && branches[branchId as string] ? branches[branchId as string] : branchId;
  };
  
  // Cihaz bilgisi modalını açma işlevi
  const handleOpenDeviceModal = (deviceInfo: any) => {
    setSelectedDeviceInfo(deviceInfo);
    setDeviceModalOpen(true);
  };

  // Cihaz bilgisi modalını kapatma işlevi
  const handleCloseDeviceModal = () => {
    setDeviceModalOpen(false);
  };
  
  // Gecikme açıklaması modalını açma işlevi
  const handleOpenDelayReasonModal = (delayReason: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Tıklamanın üst öğelere geçmesini engelle
    setSelectedDelayReason(delayReason);
    setDelayReasonModalOpen(true);
  };
  
  // Gecikme açıklaması modalını kapatma işlevi
  const handleCloseDelayReasonModal = () => {
    setDelayReasonModalOpen(false);
  };
  
  // Red nedeni modalını açma işlevi
  const handleOpenRejectReasonModal = (rejectReason: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Tıklamanın üst öğelere geçmesini engelle
    setSelectedRejectReason(rejectReason);
    setRejectReasonModalOpen(true);
  };
  
  // Red nedeni modalını kapatma işlevi
  const handleCloseRejectReasonModal = () => {
    setRejectReasonModalOpen(false);
  };
  
  // Görev yenileme modalını açma işlevi
  const handleOpenRenewTaskModal = (task: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Tıklamanın üst öğelere geçmesini engelle
    setSelectedTaskToRenew(task);
    setAdminNote("");
    setRenewTaskModalOpen(true);
  };

  // Görev yenileme modalını kapatma işlevi
  const handleCloseRenewTaskModal = () => {
    setRenewTaskModalOpen(false);
    setSelectedTaskToRenew(null);
    setAdminNote("");
  };

  // Görevi yenileme işlevi
  const handleRenewTask = async () => {
    if (!selectedTaskToRenew || !onRenewTask) return;
    
    try {
      setIsRenewing(true);
      await onRenewTask(selectedTaskToRenew.id, adminNote);
      handleCloseRenewTaskModal();
    } catch (error) {
      console.error('Görev yenilenirken hata:', error);
    } finally {
      setIsRenewing(false);
    }
  };
  
  return (
    <Box>
      <TableContainer component={Paper} sx={{ 
        mt: 2, 
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="5%">{t('tasks.status.title', 'Durum')}</TableCell>
              <TableCell width="15%">{t('tasks.name', 'Görev Adı')}</TableCell>
              {taskType === 'normal' ? (
                <>
                  <TableCell width="15%">{t('tasks.taskDate', 'Görev Tarihi')}</TableCell>
                  <TableCell width="15%">{t('tasks.description', 'Açıklama')}</TableCell>
                  <TableCell width="15%">{t('tasks.dueDate', 'Bitiş Tarihi')}</TableCell>
                </>
              ) : (
                <>
              <TableCell width="25%">{t('tasks.taskTimes', 'Görev Saatleri')}</TableCell>
                  <TableCell width="15%">{t('tasks.completionDate', 'Bitirme Tarihi')}</TableCell>
                </>
              )}
              {taskType !== 'normal' && (
              <TableCell width="10%">{t('tasks.tolerance', 'Tolerans')}</TableCell>
              )}
              <TableCell width="15%">{t('tasks.personnel', 'Personel')}</TableCell>
              <TableCell width="10%">{t('tasks.status.title', 'Durum')}</TableCell>
              <TableCell width="10%">{t('tasks.branch', 'Şube')}</TableCell>
              {statusFilter === 'completed' ? (
                <TableCell width="10%">{t('tasks.device', 'Cihaz')}</TableCell>
              ) : (
                <TableCell width="10%" align="right">{t('tasks.actions', 'İşlemler')}</TableCell>
              )}
               {statusFilter === 'completed' && <TableCell width="5%">{t('tasks.location', 'Konum')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTasks.map((task) => (
              <TableRow 
                key={task.id} 
                hover 
                onClick={() => onShowTaskDetail(task)} 
                sx={{
                  cursor: statusFilter === 'completed' || statusFilter === 'missed' ? 'default' : 'pointer'
                }}
              >
                <TableCell>
                  <Avatar
                    sx={{
                      width: 32, 
                      height: 32, 
                      bgcolor: `${getStatusColor(task.status)}20`,
                      color: getStatusColor(task.status)
                    }}
                  >
                    {getStatusIcon(task.status)}
                  </Avatar>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {task.name}
                  </Typography>
                </TableCell>
                
                {/* Görev Tarihi / Saatleri */}
                <TableCell>
                  {task.isRecurring && task.repetitionTimes && task.repetitionTimes.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {task.repetitionTimes.map((time: string, index: number) => (
                        <TaskTimeChip
                          key={index}
                          label={time}
                          status={getTaskTimeColor(task, time)}
                        />
                      ))}
                    </Box>
                  ) : task.deadline ? (
                    <Typography variant="body2" color="text.secondary">
                      {(() => {
                        try {
                          // Tarih formatını kontrol et ve uygun şekilde dönüştür
                          const deadlineDate = new Date(task.deadline);
                          // Geçerli bir tarih mi kontrol et
                          if (!isNaN(deadlineDate.getTime())) {
                            return deadlineDate.toLocaleDateString('tr-TR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }).replace(',', ' '); // Virgül yerine boşluk ekle
                          }
                          // Eğer dönüştürülemezse orijinal değeri göster
                          return task.deadline;
                        } catch (error) {
                          // Herhangi bir hata durumunda orijinal değeri göster
                          return task.deadline;
                        }
                      })()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                
                {/* Normal görevler için açıklama */}
                {taskType === 'normal' && (
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {task.description ? (task.description.length > 50 ? task.description.substring(0, 50) + '...' : task.description) : '-'}
                    </Typography>
                  </TableCell>
                )}
                
                {/* Bitiş Tarihi / Bitirme Tarihi */}
                <TableCell>
                  {task.status === 'completed' && (
                    <Typography 
                      variant="body2" 
                      color={task.wasDelayed ? "error" : "text.secondary"}
                      fontWeight={task.wasDelayed ? "medium" : "normal"}
                    >
                      {task.fromDatabase && task.completionDate 
                        ? `${task.completionDate}` 
                        : (task.completedAt ? new Date(task.completedAt).toLocaleDateString('tr-TR', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-')}
                    </Typography>
                  )}
                  {task.status === 'missed' && (
                    <Typography variant="body2" color="error" fontWeight="medium">
                      {task.fromDatabase && task.missedDate 
                        ? `${task.missedDate}` 
                        : (task.missedAt ? new Date(task.missedAt).toLocaleDateString('tr-TR', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-')}
                    </Typography>
                  )}
                  {task.status !== 'completed' && task.status !== 'missed' && (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                
                {taskType !== 'normal' && (
                <TableCell>
                  {task.isRecurring ? (
                    <Chip 
                      label={`${task.startTolerance || 15} ${t('tasks.minutes', 'dk')}`}
                      size="small"
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem', 
                        bgcolor: 'primary.50', 
                        color: 'primary.main'
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2" color={task.personnelName ? 'text.secondary' : 'error'} sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    fontSize: '0.8rem'
                  }}>
                    <PersonIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                    {task.personnelName || t('tasks.unassigned', 'Personel atanmamış')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={getStatusLabel(task.status)}
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      bgcolor: `${getStatusColor(task.status)}20`, 
                      color: getStatusColor(task.status),
                      fontWeight: 500
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    fontSize: '0.75rem'
                  }}>
                    {task.branchesId ? (
                      <>
                        <LocationOnIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                        {getBranchName(task.branchesId)}
                      </>
                    ) : (
                      '-'
                    )}
                  </Typography>
                </TableCell>
                {statusFilter === 'completed' ? (
                  <TableCell>
                    {task.deviceInfo ? (
                      <Tooltip title="Cihaz bilgilerini görüntüle">
                        <Button
                          size="small"
                          startIcon={<PhoneAndroidIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeviceModal(task.deviceInfo);
                          }}
                          sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                        >
                          {task.deviceInfo.model || task.deviceInfo.deviceType}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                        -
                      </Typography>
                    )}
                  </TableCell>
                ) : (
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                      {/* QR kod butonu */}
                      {task.isRecurring && task.completionType === 'qr' && !task.fromDatabase && (
                        <Button
                          size="small"
                          startIcon={<QrCodeIcon />}
                          variant="outlined"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenQrPrintModal(task, e);
                          }}
                          sx={{ 
                            p: 0.5, 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            height: 26,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          QR
                        </Button>
                      )}
                      
                      {/* Gecikme açıklaması butonu - Normal görevlerde ve wasDelayed veya delayReason varsa */}
                      {taskType === 'normal' && (task.wasDelayed || task.delayReason) && (
                        <Button
                          size="small"
                          startIcon={<InfoIcon />}
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            handleOpenDelayReasonModal(task.delayReason || '', e);
                          }}
                          sx={{ 
                            p: 0.5, 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            height: 26,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {t('tasks.explanation', 'Açıklama')}
                        </Button>
                      )}
                      
                      {/* Görevi Yenile butonu - Sadece reddedilen görevlerde göster */}
                      {taskType === 'normal' && task.status === 'rejected' && onRenewTask && (
                        <Button
                          size="small"
                          startIcon={<RefreshIcon />}
                          variant="outlined"
                          color="primary"
                          onClick={(e) => handleOpenRenewTaskModal(task, e)}
                          sx={{ 
                            p: 0.5, 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            height: 26,
                            whiteSpace: 'nowrap',
                            display: 'inline-flex'
                          }}
                        >
                          {t('tasks.renewTask', 'Görevi Yenile')}
                        </Button>
                      )}
                      
                      {/* Red Nedeni butonu - Sadece reddedilen görevlerde göster */}
                      {taskType === 'normal' && task.status === 'rejected' && task.rejectReason && (
                        <Button
                          size="small"
                          startIcon={<CancelIcon />}
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            handleOpenRejectReasonModal(task.rejectReason || '', e);
                          }}
                          sx={{ 
                            p: 0.5, 
                            minWidth: 'auto', 
                            fontSize: '0.7rem',
                            height: 26,
                            whiteSpace: 'nowrap',
                            display: 'inline-flex'
                          }}
                        >
                          {t('tasks.rejectReason', 'Red Nedeni')}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                )}
                {statusFilter === 'completed' && (
                    <TableCell>
                        {task.completionLocation ? (
                            <IconButton color="primary" onClick={(e) => {
                                e.stopPropagation();
                                if(onOpenTaskLocationModal) onOpenTaskLocationModal(task);
                            }}>
                                <Navigation />
                            </IconButton>
                        ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                    </TableCell>
                )}
              </TableRow>
            ))}
            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={taskType === 'normal' ? 9 : 9} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {t('tasks.noTasks', 'Görev bulunamadı')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Sayfalama */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <TablePagination
          component="div"
          count={tasks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage={t('tasks.pagination.rowsPerPage', 'Sayfa başına satır:')}
          labelDisplayedRows={({ from, to, count }) => 
            t('tasks.pagination.displayedRows', '{{count}} kayıttan {{from}}-{{to}} arası gösteriliyor', {
              count: count,
              from: from,
              to: to
            })
          }
        />
      </Box>

      {/* Cihaz Bilgileri Modalı */}
      <DeviceInfoModal
        open={deviceModalOpen}
        onClose={handleCloseDeviceModal}
        deviceInfo={selectedDeviceInfo}
      />
      
      {/* Gecikme Açıklaması Modalı */}
      <Dialog
        open={delayReasonModalOpen}
        onClose={handleCloseDelayReasonModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
          {t('tasks.delayReason', 'Gecikme Açıklaması')}
          <IconButton
            aria-label="kapat"
            onClick={handleCloseDelayReasonModal}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'error.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            {selectedDelayReason || "Açıklama bulunamadı."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelayReasonModal} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Red Nedeni Modalı */}
      <Dialog
        open={rejectReasonModalOpen}
        onClose={handleCloseRejectReasonModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.light', 
          color: 'error.contrastText',
          display: 'flex',
          alignItems: 'center'
        }}>
          <CancelIcon sx={{ mr: 1 }} />
          Görev Reddetme Nedeni
          <IconButton
            aria-label="kapat"
            onClick={handleCloseRejectReasonModal}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'error.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            {selectedRejectReason || "Açıklama bulunamadı."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectReasonModal} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Görev Yenileme Onay Modalı */}
      <Dialog
        open={renewTaskModalOpen}
        onClose={handleCloseRenewTaskModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.light', 
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center'
        }}>
          <RefreshIcon sx={{ mr: 1 }} />
          Görevi Yenile
          <IconButton
            aria-label="kapat"
            onClick={handleCloseRenewTaskModal}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 1 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            {selectedTaskToRenew?.name || 'Görev'}
          </Typography>
          
          <Box sx={{ bgcolor: '#fff5f7', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Red Nedeni:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {selectedTaskToRenew?.rejectReason || 'Belirtilmemiş'}
            </Typography>
          </Box>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Personel bu görevi reddetmiştir. Görevi personele yeniden göndermek istediğinizden emin misiniz? 
            <Typography component="span" fontWeight="bold" color="primary.main"> Personel bu görevi yeniden reddedemeyecek.</Typography>
          </Typography>
          
          <TextField
            margin="normal"
            fullWidth
            label="Açıklama (Personele İletilecek)"
            multiline
            rows={3}
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Personele iletmek istediğiniz bir açıklama yazabilirsiniz..."
            variant="outlined"
            required
            error={adminNote.trim() === ''}
            helperText={adminNote.trim() === '' ? "Personele iletilecek açıklama gerekli" : ""}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseRenewTaskModal} 
            color="inherit"
            disabled={isRenewing}
          >
            İptal
          </Button>
          <Button 
            onClick={handleRenewTask} 
            variant="contained" 
            color="primary"
            disabled={isRenewing || adminNote.trim() === ''}
            startIcon={isRenewing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {isRenewing ? 'Yenileniyor...' : 'Görevi Yenile'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TasksTable; 